import { computed, ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFocusMode } from 'dashboard/composables/useFocusMode';

const LIST_AVATAR = 64;
const LIST_COMPACT = 256;
const LIST_COMFORTABLE = 340;
const LIST_HIDE_AT = 40;
const LIST_AVATAR_AT = 160;
const LIST_COMPACT_AT = 300;
const RIGHT_OPEN = 320;
const RIGHT_CLOSE_AT = 160;

const listDragWidth = ref(null);
const rightDragWidth = ref(null);

let listStartX = 0;
let listStartW = 0;
let rightStartX = 0;
let rightStartW = 0;
let draggingList = false;
let draggingRight = false;
let listenersBound = false;
let snapListWidthFn = () => {};
let snapRightWidthFn = () => {};

const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onPointerMove = event => {
  if (draggingList) {
    const delta = getClientX(event) - listStartX;
    listDragWidth.value = Math.max(0, Math.min(420, listStartW + delta));
    if (event.cancelable) event.preventDefault();
  }
  if (draggingRight) {
    const delta = rightStartX - getClientX(event);
    rightDragWidth.value = Math.max(0, Math.min(400, rightStartW + delta));
    if (event.cancelable) event.preventDefault();
  }
};

const onPointerUp = () => {
  if (!draggingList && !draggingRight) return;
  if (draggingList) snapListWidthFn(listDragWidth.value ?? 0);
  if (draggingRight) snapRightWidthFn(rightDragWidth.value ?? 0);
  listDragWidth.value = null;
  rightDragWidth.value = null;
  draggingList = false;
  draggingRight = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
};

const bindDragListeners = () => {
  if (listenersBound || typeof document === 'undefined') return;
  listenersBound = true;
  document.addEventListener('mousemove', onPointerMove);
  document.addEventListener('mouseup', onPointerUp);
  document.addEventListener('touchmove', onPointerMove, { passive: false });
  document.addEventListener('touchend', onPointerUp);
};

export function useWorkbenchLayout() {
  const { uiSettings, updateUISettings } = useUISettings();
  const { isFocusModeEnabled } = useFocusMode();

  bindDragListeners();

  const listMode = computed(() => {
    const mode = uiSettings.value?.workbench_list_mode;
    if (mode === 'avatars' || mode === 'hidden' || mode === 'list') return mode;
    if (
      isFocusModeEnabled.value &&
      uiSettings.value?.workbench_list_peek !== true
    ) {
      return 'hidden';
    }
    return 'list';
  });

  const isListCompact = computed(() => {
    if (isFocusModeEnabled.value) return true;
    return uiSettings.value?.workbench_list_compact === true;
  });

  const isAvatarRail = computed(() => {
    if (listDragWidth.value != null) {
      return (
        listDragWidth.value > LIST_HIDE_AT &&
        listDragWidth.value < LIST_AVATAR_AT
      );
    }
    return isFocusModeEnabled.value && listMode.value === 'avatars';
  });

  const isListHidden = computed(() => {
    if (listDragWidth.value != null && listDragWidth.value > LIST_HIDE_AT) {
      return false;
    }
    return isFocusModeEnabled.value && listMode.value === 'hidden';
  });

  const isRightOpen = computed(
    () => uiSettings.value?.is_contact_sidebar_open === true
  );

  const setListMode = mode => {
    updateUISettings({
      workbench_list_mode: mode,
      workbench_list_peek: mode !== 'hidden',
      workbench_list_compact: true,
    });
  };

  const snapListWidth = px => {
    if (isFocusModeEnabled.value) {
      if (px < LIST_HIDE_AT) {
        setListMode('hidden');
        return;
      }
      if (px < LIST_AVATAR_AT) {
        setListMode('avatars');
        return;
      }
      setListMode('list');
      return;
    }
    updateUISettings({
      workbench_list_compact: px < LIST_COMPACT_AT,
    });
  };

  const snapRightWidth = px => {
    updateUISettings({ is_contact_sidebar_open: px >= RIGHT_CLOSE_AT });
  };

  snapListWidthFn = snapListWidth;
  snapRightWidthFn = snapRightWidth;

  const toggleListCompact = () => {
    if (isFocusModeEnabled.value) {
      updateUISettings({
        workbench_list_peek: uiSettings.value?.workbench_list_peek !== true,
      });
      return;
    }
    updateUISettings({
      workbench_list_compact: !uiSettings.value?.workbench_list_compact,
    });
  };

  const startListDrag = event => {
    draggingList = true;
    listStartX = getClientX(event);
    if (isListHidden.value) {
      listStartW = 0;
    } else if (isAvatarRail.value) {
      listStartW = LIST_AVATAR;
    } else if (isListCompact.value) {
      listStartW = LIST_COMPACT;
    } else {
      listStartW = LIST_COMFORTABLE;
    }
    listDragWidth.value = listStartW;
    Object.assign(document.body.style, {
      cursor: 'col-resize',
      userSelect: 'none',
    });
    event.preventDefault();
  };

  const startRightDrag = event => {
    draggingRight = true;
    rightStartX = getClientX(event);
    rightStartW = isRightOpen.value ? RIGHT_OPEN : 0;
    rightDragWidth.value = rightStartW;
    Object.assign(document.body.style, {
      cursor: 'col-resize',
      userSelect: 'none',
    });
    event.preventDefault();
  };

  return {
    listMode,
    isListCompact,
    isAvatarRail,
    isListHidden,
    isRightOpen,
    listDragWidth,
    rightDragWidth,
    setListMode,
    toggleListCompact,
    startListDrag,
    startRightDrag,
  };
}
